import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Edit, Trash2, Upload, Plus, Globe } from "lucide-react";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useGeographicBoundaries,
  useDeleteGeographicBoundary,
  useFetchFromOSM,
} from "@/api/geographic-boundaries";
import type { GeographicBoundary } from "@/api.types.v2";
import { UploadGeoJSONDialog } from "./upload-geojson-dialog";
import { CreateGeographicBoundaryDialog } from "./create-geographic-boundary-dialog";
import { GeographicBoundariesMap } from "./geographic-boundaries-map";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export function GeographicBoundariesTable() {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingBoundary, setEditingBoundary] =
    useState<GeographicBoundary | null>(null);
  const [showOsmInput, setShowOsmInput] = useState(false);
  const [osmRelationId, setOsmRelationId] = useState("");

  const {
    data: geographicBoundaries,
    isLoading,
    isError,
    refetch,
  } = useGeographicBoundaries();
  const deleteBoundaryMutation = useDeleteGeographicBoundary({
    onSuccess: () => {
      toast.success("Geographic boundary deleted successfully");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to delete geographic boundary");
    },
  });

  const fetchFromOSMMutation = useFetchFromOSM({
    onSuccess: (result) => {
      toast.success(`Successfully fetched boundary: ${result.boundary.name}`);
      void refetch();
      setOsmRelationId("");
      setShowOsmInput(false);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to fetch from OpenStreetMap");
    },
  });

  const handleDelete = (geographicBoundaryId: string) => {
    if (confirm("Are you sure you want to delete this geographic boundary?")) {
      deleteBoundaryMutation.mutate(geographicBoundaryId);
    }
  };

  const handleFetchFromOSM = () => {
    if (osmRelationId) {
      fetchFromOSMMutation.mutate(osmRelationId);
    }
  };

  return (
    <>
      <div className="mb-6">
        <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
          <div>
            <h3 className="text-lg font-semibold">Geographic Boundaries</h3>
            <p className="text-sm text-gray-600">
              Manage geographic boundaries from external sources like
              OpenStreetMap
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => setCreateDialogOpen(true)}
              size="sm"
              variant="outline"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Boundary
            </Button>
            <Button
              onClick={() => setShowOsmInput(!showOsmInput)}
              size="sm"
              variant="outline"
            >
              <Globe className="h-4 w-4 mr-2" />
              Fetch from OSM
            </Button>
            <Button onClick={() => setUploadDialogOpen(true)} size="sm">
              <Upload className="h-4 w-4 mr-2" />
              Upload GeoJSON
            </Button>
          </div>
        </div>

        {showOsmInput && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder="Enter OSM relation ID (e.g., 2407259 for Richmond Hill)"
                value={osmRelationId}
                onChange={(e) => setOsmRelationId(e.target.value)}
                className="flex-1 bg-white"
              />
              <Button
                onClick={handleFetchFromOSM}
                disabled={!osmRelationId || fetchFromOSMMutation.isPending}
              >
                {fetchFromOSMMutation.isPending ? "Fetching..." : "Fetch"}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowOsmInput(false);
                  setOsmRelationId("");
                }}
              >
                Cancel
              </Button>
            </div>
            <p className="text-xs text-blue-700 mt-2">
              Find relation IDs on{" "}
              <a
                href="https://www.openstreetmap.org"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                OpenStreetMap.org
              </a>
              . Search for a place and look for "Relation: " in the details.
            </p>
          </div>
        )}

        {isLoading ? (
          <p>Loading geographic boundaries...</p>
        ) : isError ? (
          <div role="alert" className="space-y-2">
            <p>Unable to load geographic boundaries. Please try again.</p>
            <Button variant="outline" onClick={() => void refetch()}>
              Retry
            </Button>
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Boundary Points</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {geographicBoundaries?.map((boundary) => (
                  <TableRow key={boundary.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{boundary.name}</p>
                        {boundary.metadata?.population != null && (
                          <p className="text-sm text-gray-500">
                            Population:{" "}
                            {Number(
                              boundary.metadata.population
                            ).toLocaleString()}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                        {boundary.code ?? "N/A"}
                      </code>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {boundary.source === "osm"
                          ? "OpenStreetMap"
                          : boundary.source}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="secondary"
                        className={`${
                          boundary.isActive ? "bg-green-500" : "bg-gray-500"
                        } text-white`}
                      >
                        {boundary.isActive ? "ACTIVE" : "INACTIVE"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm">
                        {(() => {
                          if (!boundary.boundary?.coordinates)
                            return "0 points";
                          let pointCount = 0;
                          if (boundary.boundary.type === "Polygon") {
                            pointCount = boundary.boundary.coordinates.reduce(
                              (sum: number, ring: number[][]) =>
                                sum + ring.length,
                              0
                            );
                          } else {
                            // MultiPolygon
                            pointCount = boundary.boundary.coordinates.reduce(
                              (sum: number, polygon: number[][][]) =>
                                sum +
                                polygon.reduce(
                                  (ringSum: number, ring: number[][]) =>
                                    ringSum + ring.length,
                                  0
                                ),
                              0
                            );
                          }
                          return `${pointCount.toLocaleString()} points`;
                        })()}
                      </p>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm">
                        {new Date(boundary.createdAt).toLocaleDateString()}
                      </p>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingBoundary(boundary)}
                          title="Edit Geographic Boundary"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(boundary.id)}
                          title="Delete Geographic Boundary"
                          disabled={deleteBoundaryMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {(!geographicBoundaries ||
                  geographicBoundaries.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="text-gray-500">
                        <p>No geographic boundaries found</p>
                        <p className="text-sm">
                          Add a boundary, fetch from OpenStreetMap, or upload
                          GeoJSON files to get started
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Geographic Boundaries Map */}
      {!isLoading && !isError && (
        <div className="mb-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold">Geographic Boundaries Map</h3>
            <p className="text-sm text-gray-600">
              Visual representation of all geographic boundaries. Click on any
              boundary to see details.
            </p>
          </div>
          <GeographicBoundariesMap height="500px" />
        </div>
      )}

      <UploadGeoJSONDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
      />

      <CreateGeographicBoundaryDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />

      <Dialog
        open={!!editingBoundary}
        onOpenChange={() => setEditingBoundary(null)}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Geographic Boundary</DialogTitle>
          </DialogHeader>
          {editingBoundary && (
            <CreateGeographicBoundaryDialog
              open={true}
              onOpenChange={() => setEditingBoundary(null)}
              defaultValues={editingBoundary}
              mode="edit"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
